<template>
  <div
    class="repchart"
    :class="{
      'repchart_no-overflow': flopGroup == 'swv_flops' && !overflowColumns,
    }"
    :style="`${chartMounted ? 'opacity:1;' : 'opacity:0;'}${
      flopGroup === 'swv_flops'
        ? '--legend-height: 5rem;'
        : '--legend-height: 3.5rem; max-height: 42rem; margin: auto 0; --controls-height: 4.75rem;'
    }`"
    style="transition: opacity 0.1s ease-in-out"
  >
    <div
      ref="cont"
      class="repchart_chart"
      :class="{
        repchart_chart_flipped: viewProps.heroLeft,
        repchart_chart_split: viewProps.chartSplit,
      }"
    >
      <div class="repchart_chart_players">
        <div class="repchart_chart_players_player repchart_chart_players_player_top">
          <i
            class="mx-auto mdi mdi-crown"
            :class="{
              opacity_0:
                (viewProps.colValue.val === 'opponent' || viewProps.colValue.val === 'hero_opponent'
                  ? opponent.position
                  : hero.position) !== hero.position,
            }"
          />
          {{
            viewProps.colValue.val === 'opponent' || viewProps.colValue.val === 'hero_opponent'
              ? opponent.position
              : hero.position
          }}
        </div>
        <div
          v-if="viewProps.colValue.val === 'hero_opponent'"
          class="repchart_chart_players_player repchart_chart_players_player_bottom"
        >
          <i class="mx-auto mdi mdi-crown" />
          {{ hero.position }}
        </div>
      </div>
      <div class="repchart_chart_cont">
        <div class="repchart_chart_cont_top_legend">
          <div
            v-for="(item, index) in totalsItems"
            :key="index"
            class="f-center weight_700"
            style="gap: 0.5rem"
          >
            <div
              v-if="index === 0"
              class="d-flex text-nowrap"
            >
              {{
                $t('common.oop_vs_ip', {
                  oop: viewProps.oop_player.position,
                  ip: viewProps.ip_player.position,
                })
              }}
            </div>
            <div
              v-if="index === 1"
              class="circle-bullet"
            ></div>
            <div
              v-if="index === 1"
              class="d-flex"
            >
              <i class="mr-1 my-auto mdi mdi-filter"></i>
              <div
                class="d-flex csst"
                @mouseenter="e => fl_positionTooltip(e, { placement: 'bottom' })"
              >
                {{ numFixed(item.ratio * 100, 1) }}%
                <div
                  class="csst_content text-nowrap clr-white"
                  style="position: fixed"
                >
                  {{ $t('tooltips.how_many_flops') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="repchart_chart_wrapper"
          data-tst="repchart"
        >
          <RepChartAxes
            class="repchart_chart_axes"
            :viewProps="viewProps"
            :eveqeqrAxes="eveqeqrAxes"
            :bettingInfo
            :class="[
              {
                repchart_axes_split: viewProps.chartSplit,
              },
            ]"
          />

          <VirtualList
            v-if="flopGroup === 'swv_flops'"
            :key="viewProps.scrollerKey + navResizeDeps"
            ref="scroller"
            :hasDynamicSize="false"
            :items="items"
            :estimateSize="colWidth"
            :itemSize="colWidth"
            idKey="id"
            class="repchart_chart_scroller local-scroller no-scroll-track"
            direction="horizontal"
            :keeps="77"
            @wheel.stop.prevent="wheelScroll"
            @touchend="handleTouchend"
            @scroll="handleScroll"
            @visible="handleChartMounted"
          >
            <template #item="{ item }">
              <RepChartColumn
                :class="{
                  repchart_column_split: viewProps.chartSplit,
                }"
                :style="`width:${colWidth}px;`"
                :item="item"
                :reportItemId="reportItemId"
                :viewProps="viewProps"
                :useHex="useHex"
                :legendVertical="legendVertical"
                @visible="handleChartMounted"
              >
                <template #col_name>
                  <div
                    class="gw_hvr gw_hvr_pointer_coarse_disabled f-center f-column w-100 h-100"
                    :class="{ repchart_column_legend_vertical: legendVertical }"
                  >
                    <a
                      v-if="hasLinks"
                      class="gw_btn_hover_back"
                      style="
                        padding: 0 0.25rem;
                        --btn-back-clr-back: var(--clr-black-1-3);
                        --btn-back-z-index: -1;
                      "
                      :href="
                        links_getLink({
                          namespace,
                          pathName: 'StudySolutions',
                          customQuery: {
                            board: `${flopGroup === 'swv_turns' ? flopBoard : ''}${item.id}`,
                            soltab: 'strategy',
                            ...(linkParams || {}),
                          },
                        })
                      "
                      target="_blank"
                    >
                      <div :class="`color-default-${item.id[1]}`">
                        {{ item.id[0] }}
                      </div>
                      <div :class="`color-default-${item.id[3]}`">
                        {{ item.id[2] }}
                      </div>
                      <div :class="`color-default-${item.id[5]}`">
                        {{ item.id[4] }}
                      </div>
                    </a>
                    <div v-else>
                      <div :class="`color-default-${item.id[1]}`">
                        {{ item.id[0] }}
                      </div>
                      <div :class="`color-default-${item.id[3]}`">
                        {{ item.id[2] }}
                      </div>
                      <div :class="`color-default-${item.id[5]}`">
                        {{ item.id[4] }}
                      </div>
                    </div>
                    <StudyReportsLinks
                      v-if="hasLinks"
                      class="gw_hvr_content"
                      :namespace
                      :item="item"
                      :flopGroup="flopGroup"
                      :flopBoard="flopBoard"
                      :linkParams="linkParams"
                      style="
                        position: absolute;
                        bottom: var(--legend-height);
                        gap: 0;
                        --flex-direction: column-reverse;
                      "
                    />
                  </div>
                </template>
              </RepChartColumn>
            </template>
          </VirtualList>

          <div
            v-else
            ref="scroller"
            class="repchart_chart_simple no-scroll-track"
            style="--gap: 1px"
            @touchend="handleTouchend"
            @wheel.stop="e => hscrl_wheelScroll(e)"
          >
            <RepChartColumn
              v-for="item in items"
              :key="item.id"
              class="repchart_chart_simple_column"
              :class="[
                {
                  repchart_column_split: viewProps.chartSplit,
                },
                item.cssClass,
              ]"
              :item="item"
              :reportItemId="reportItemId"
              :viewProps="viewProps"
              :useHex="useHex"
              :legendVertical="legendVertical"
            >
              <template #col_name="{ item }">
                <div
                  v-if="flopGroup === 'swv_turns'"
                  class="gw_hvr gw_hvr_pointer_coarse_disabled f-center f-column w-100 h-100"
                  target="_blank"
                  :class="{ repchart_column_legend_vertical: legendVertical }"
                >
                  <a
                    class="gw_btn_hover_back"
                    style="
                      padding: 0.5rem 0.25rem;
                      --btn-back-clr-back: var(--clr-black-1-3);
                      --btn-back-z-index: -1;
                    "
                    :href="
                      links_getLink({
                        namespace,
                        pathName: 'StudySolutions',
                        customQuery: {
                          board: `${flopGroup === 'swv_turns' ? flopBoard : ''}${item.id}`,
                          soltab: 'strategy',
                        },
                      })
                    "
                    target="_blank"
                  >
                    <div :class="`color-default-${item.id[1]}`">
                      {{ item.id[0] }}
                    </div>
                  </a>
                  <StudyReportsLinks
                    class="gw_hvr_content"
                    :namespace
                    :item="item"
                    :flopGroup="flopGroup"
                    :flopBoard="flopBoard"
                    style="
                      position: absolute;
                      bottom: var(--legend-height);
                      --flex-direction: column-reverse;
                      gap: 0;
                    "
                  />
                </div>
                <div v-else>
                  <div
                    v-if="flopGroup === 'swv_suits'"
                    class="f-center"
                  >
                    <div
                      v-for="(letter, idx) in suitsSymbols[item.id]
                        ? suitsSymbols[item.id]
                        : [item.id[0]]"
                      :key="`${letter}_${idx}`"
                      class="mdi text-semi-large"
                      :class="suitsConf[letter].icon"
                      style="height: 1.325rem"
                      :style="`color:var(${suitsConf[letter].colorDefault})`"
                    ></div>
                  </div>
                  {{ $t(`practice.filter.${item.id}`) }}
                </div>
              </template>
            </RepChartColumn>
          </div>
        </div>

        <RepChartNav
          v-if="hasNav && overflowColumns"
          class="repchart_nav swp_cont_stop"
          :hasLegend="overflowColumns"
          :hasHandle="flopGroup === 'swv_flops' ? overflowColumns : false"
          style="--height: var(--nav-height)"
          :items="items"
          :useHex="useHex"
          :showBetsIn="showBetsIn"
          :grouping="grouping"
          :viewProps="viewProps"
          :colWidth="colWidth"
          :scrolledPercent="scrolledPercent"
          :hero="hero"
          :opponent="opponent"
          :navResizeDeps="navResizeDeps"
          @touchend.stop
          @scroll:update="handleScrollUpdate"
        ></RepChartNav>
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import StudyReportsLinks from '@/components/Study/StudyReportsLinks'
import { getSuitsSymbols, getSuitsConf } from '@/helpers/constants'
import VirtualList from '@/components/vue3/VirtualList/VirtualList'
import RepChartNav from '@/components/DataTables/v2/tables/reports/RepChartNav'
import RepChartColumn from '@/components/DataTables/v2/tables/reports/RepChartColumn'
import RepChartAxes from '@/components/DataTables/v2/tables/reports/RepChartAxes'
import links_mix from '@/mixins/links_mix'
import format_mix from '@/mixins/format_mix'
import float_mix from '@/mixins/float_mix'
import horizontalScroll_mix from '@/components/HorizolnalScroller/horizontalScroll_mix'

export default {
  name: 'RepChart',
  components: {
    VirtualList,
    RepChartColumn,
    RepChartNav,
    RepChartAxes,
    StudyReportsLinks,
  },
  mixins: [format_mix, float_mix, links_mix, horizontalScroll_mix],
  props: {
    namespace: { required: true, type: String },
    items: Array,
    totalsItems: Array,
    legendVertical: Boolean,
    useHex: Boolean,
    bettingInfo: Object,
    playerRelPosition: { default: 'ip' },
    reportItemId: { default: '' },
    hero: Object,
    opponent: Object,
    colStyleKey: String,
    viewProps: Object,
    grouping: String,
    showBetsIn: String,
    solution: Array,
    flopGroup: String,
    navResizeDeps: Object,
    flopBoard: String,
    eveqeqrAxes: Object,
    hasNav: Boolean,
    hasLinks: Boolean,
    linkParams: Object,
  },
  data() {
    this.suitsSymbols = getSuitsSymbols()
    this.suitsConf = getSuitsConf()
    // this.colWidth = 23
    return {
      scrolledPercent: 0,
      overflowColumns: true,
      chartMounted: false,
      hscrl_surface: null,
    }
  },
  created() {
    if (this.flopGroup !== 'swv_flops') {
      this.chartMounted = true
    }
  },
  methods: {
    handleTouchend(e) {
      const scroller = this.$refs.scroller?.$el || this.$refs.scroller
      if (!scroller) return
      // add info on event for parent handling swipe events
      const timestamp = Date.now()
      const ist_top = scroller.scrollTop <= 0
      const ist_bottom = scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight
      const ist_left = scroller.scrollLeft <= 0
      const ist_right = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth

      const ist_top_time =
        this.hscrl_surface?.ist_to === ist_top ? this.hscrl_surface.ist_top_time || 0 : timestamp
      const ist_bottom_time =
        this.hscrl_surface?.ist_bottom === ist_bottom
          ? this.hscrl_surface.ist_bottom_time || 0
          : timestamp
      const ist_left_time =
        this.hscrl_surface?.ist_left === ist_left
          ? this.hscrl_surface.ist_left_time || 0
          : timestamp
      const ist_right_time =
        this.hscrl_surface?.ist_right === ist_right
          ? this.hscrl_surface.ist_right_time || 0
          : timestamp

      const surface = {
        timestamp,
        ist_first: !this.hscrl_surface,
        ist_top,
        ist_bottom,
        ist_left,
        ist_right,
        ist_top_time,
        ist_bottom_time,
        ist_left_time,
        ist_right_time,
      }

      this.hscrl_surface = surface
      e.gw_touchends = [...(e.gw_touchends || []), surface]
    },
    wheelScroll(e) {
      // TODO find out if the the scroll jump can be avoided - reproduce by carefully scrolling
      // till treshold then it jumps by a few px but it shouldnt
      // as far as I know it happens only here and not when using native scroll
      // problem in vue2 as well
      const cont = this.$refs.scroller
      if (cont) {
        const element = cont.$el
        if (Math.abs(e.deltaY) === Math.abs(e.deltaX)) {
          return
        }
        const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX
        element.scrollLeft += delta * 2
      }
    },

    // ======
    handleChartMounted() {
      this.handleScrollUpdate({ percentX: this.$store.getters.rep_data_scrolledPercent })
      this.checkOverflow()
      this.chartMounted = true
    },
    handleScroll: throttle(function (e) {
      const { scrollLeft, scrollWidth, clientWidth } = e.target
      this.scrolledPercent = scrollLeft / (scrollWidth - clientWidth)
      // console.log(this.scrolledPercent, { scrollLeft, scrollWidth, clientWidth })
      this.persistScroll()
    }, 1),
    persistScroll: throttle(function (e) {
      this.$store.commit('reports_set', { key: 'scrolledPercent', value: this.scrolledPercent })
    }, 40),
    handleScrollUpdate({ percentX } = {}) {
      const scroller = this.$refs?.scroller?.$el
      if (scroller) {
        const { scrollWidth, clientWidth } = scroller
        const res = (scrollWidth - clientWidth) * percentX
        scroller.scrollLeft = res
      }
    },
    checkOverflow() {
      this.overflowColumns = true
      const cont = this.$refs?.cont
      if (cont) {
        const visibleColumns = cont.clientWidth / this.colWidth
        this.overflowColumns = this.items.length - 4 > visibleColumns
      }
    },
  },
  computed: {
    scrollerKey() {
      return this.viewProps.scrollerKey
    },
    colWidth() {
      if (this.$store.getters.globals_window_size) {
        const fntSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize)
        return fntSizePx * 1.575
        // return Math.ceil(fntSizePx * 1.575)
      }
      return 24
      // return 54
    },
  },
  watch: {
    viewProps() {
      this.checkOverflow()
    },
    navResizeDeps() {
      this.checkOverflow()
    },
    scrollerKey() {
      this.scrolledPercent = 0
      this.persistScroll()
    },
  },
}
</script>

<style lang="scss">
.repchart_chart {
  .vue-recycle-scroller__item-view {
    max-width: var(--col-width);
  }
}
</style>

<style lang="scss" scoped>
.repchart {
  --col-width: 23px;
  --nav-height: 3rem;
  max-height: auto;
  --controls-height: 0px;
  --legend-height: 5rem;
  --gap: 0.5rem;

  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  &_no-overflow {
    .repchart_chart {
      justify-content: center;
      &_cont {
        flex: unset;
      }
    }
  }
  &_chart {
    flex: 1;
    gap: var(--gap);
    display: flex;
    &_players {
      width: 3rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      height: calc(100% - var(--nav-height) - var(--controls-height) - var(--gap));
      margin-top: var(--controls-height);
      &_player {
        height: calc(var(--legend-height));
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: var(--clr-grey-2);
        line-height: 1;
        gap: 0.25rem;
        font-weight: 700;
        font-size: 0.875rem;
      }
    }
    &_wrapper {
      flex: 1;
      display: flex;
      min-height: 0;
      min-width: 0;
      position: relative;
      overflow: hidden;
      margin: auto 0;
      padding-left: 2.5rem;
      margin-left: -2.5rem;
    }
    &_cont {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: var(--gap);
      min-height: 0;
      min-width: 0;
      &_top {
        &_legend {
          display: flex;
          gap: 0.5rem;
          height: 2rem;
          color: var(--clr-grey-2);
        }
      }
    }
    &_simple {
      flex: 1;
      display: flex;
      min-height: 0;
      min-width: 0;
      // margin: auto 0;
      overflow: auto;
      &_column {
        margin-right: var(--gap);
        flex: 1;
        &.chart_first-item {
          margin-left: 0.25rem;
        }
      }
    }
    &_axes {
      position: relative;
      z-index: 4;
    }
    &_scroller {
      flex: 1;
      min-height: 0;
      min-width: 0;
      max-width: 100vw;
    }
  }
  &_chart {
    &_flipped {
      .repchart_chart_players {
        justify-content: flex-start;
        flex-direction: column-reverse;
      }
    }
    &_split {
      .repchart_chart_players {
        justify-content: center;
      }
    }
  }
}

@media screen and (max-width: 1023px) {
  .repchart {
    --nav-height: 2rem;
    &_chart {
      &_players {
        width: 2rem;
      }
    }
  }
}
</style>
